import React from 'react';

// Import RenderRow from DynamicPage - we'll inline a simplified version
// to avoid circular dependency

const containerWidthMap = { narrow: 800, default: 1200, wide: 1400 };

function RenderElement({ el }) {
  if (!el) return null;
  const style = { textAlign: el.align || 'left' };

  switch (el.type) {
    case 'heading': {
      const Tag = el.headingLevel || 'h2';
      return React.createElement(Tag, {
        style: {
          ...style,
          fontSize: el.fontSize ? `${el.fontSize}px` : '28px',
          color: el.color || 'inherit',
          fontWeight: el.bold !== false ? 700 : 400,
          margin: 0,
          marginBottom: el.marginBottom ?? 12,
          textTransform: el.textTransform || 'none',
        }
      }, el.content);
    }
    case 'text': {
      const textStyle = {
        ...style,
        lineHeight: el.lineHeight ?? 1.8,
        color: el.color || 'inherit',
        fontSize: el.fontSize ? `${el.fontSize}px` : '16px',
        fontWeight: el.bold ? 700 : 400,
        textTransform: el.textTransform || 'none',
      };
      return <div style={textStyle} dangerouslySetInnerHTML={{ __html: el.content }} />;
    }
    case 'image':
      if (!el.src) return null;
      return (
        <div style={{ ...style, maxWidth: el.maxWidth || '100%', borderRadius: (el.borderRadius ?? 12) + 'px', overflow: 'hidden', ...(el.align === 'center' ? { margin: '0 auto' } : {}) }}>
          <img src={el.src} alt={el.altText || ''} style={{ width: '100%', objectFit: el.objectFit || 'cover', display: 'block' }} />
        </div>
      );
    case 'video':
      if (!el.src) return null;
      if (el.src.includes('youtube') || el.src.includes('youtu.be')) {
        return <iframe src={el.src.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')} style={{ width: '100%', aspectRatio: '16/9', border: 'none', borderRadius: 12 }} allowFullScreen title="Video" />;
      }
      return <video src={el.src} controls style={{ width: '100%', borderRadius: 12 }} />;
    case 'button': {
      const pad = el.size === 'sm' ? '8px 20px' : el.size === 'lg' ? '16px 44px' : '12px 32px';
      const bg = el.outlined ? 'transparent' : (el.bgColor || 'var(--color-primary)');
      const border = el.outlined ? `2px solid ${el.bgColor || 'var(--color-primary)'}` : 'none';
      const color = el.outlined ? (el.bgColor || 'var(--color-primary)') : (el.textColor || '#fff');
      return (
        <div style={style}>
          <a href={el.buttonLink || '#'} target={el.buttonTarget || '_self'} style={{
            display: el.btnFullWidth ? 'block' : 'inline-block', padding: pad, background: bg, color, border,
            borderRadius: (el.btnBorderRadius ?? 30) + 'px', textDecoration: 'none', fontWeight: 600, textAlign: 'center',
          }}>{el.buttonText || 'Click'}</a>
        </div>
      );
    }
    case 'spacer':
      return <div style={{ height: el.content ? `${el.content}px` : '40px' }} />;
    case 'divider':
      return <div style={{ margin: `${el.marginTop ?? 20}px auto ${el.marginBottom ?? 20}px`, width: el.dividerWidth || '100%', height: el.thickness ?? 1, background: el.dividerColor || '#333' }} />;
    case 'html':
      return <div dangerouslySetInnerHTML={{ __html: el.content }} />;
    default:
      return null;
  }
}

function HomeDynamicSection({ page }) {
  if (!page || !page.rows || page.rows.length === 0) return null;

  const verticalAlignMap = { top: 'flex-start', center: 'center', bottom: 'flex-end' };

  return (
    <div id={`dynamic-${page.slug}`}>
      {page.rows.map((row, ri) => {
        const hasBgImage = !!row.bgImage;
        const hasBgColor = !!row.bgColor;
        const cw = containerWidthMap[row.containerWidth || 'default'];

        const outerStyle = {
          position: 'relative',
          overflow: 'hidden',
          ...(hasBgImage ? {
            backgroundImage: `url(${row.bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          } : {}),
          ...(hasBgColor && !hasBgImage ? { background: row.bgColor } : {}),
        };

        const innerStyle = {
          position: 'relative',
          maxWidth: row.containerWidth === 'full' ? '100%' : (cw || 1200),
          margin: '0 auto',
          padding: `${row.paddingTop ?? 60}px 24px ${row.paddingBottom ?? 60}px`,
          display: 'grid',
          gridTemplateColumns: `repeat(${row.columns || 1}, 1fr)`,
          gap: (row.gap ?? 24) + 'px',
          alignItems: verticalAlignMap[row.verticalAlign || 'center'] || 'center',
          textAlign: row.textAlign || 'left',
        };

        return (
          <div key={ri} style={outerStyle}>
            {hasBgImage && (
              <div style={{
                position: 'absolute', inset: 0,
                background: row.bgOverlayColor || '#000',
                opacity: row.bgOverlayOpacity ?? 0.5, zIndex: 0,
              }} />
            )}
            <div style={innerStyle}>
              {(row.elements || []).slice(0, row.columns || 1).map((el, ei) => (
                <div key={ei} style={{ position: 'relative', zIndex: 1 }}>
                  <RenderElement el={el} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default HomeDynamicSection;
